"use client";

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getAdminMetrics } from "@/lib/requestor/admin-commerce";
import { AdminMetrics } from "@/lib/types";
import { formatAmountFen, formatTime } from "@/lib/utils";

export default function DashboardPage() {
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getAdminMetrics();
      setMetrics(data);
    } catch (error: any) {
      toast.error(error?.message || "加载仪表盘失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const kpis = [
    { label: "今日创建订单", value: metrics?.order_created_today || 0 },
    { label: "今日支付订单", value: metrics?.order_paid_today || 0 },
    { label: "近7日支付订单", value: metrics?.order_paid_7d || 0 },
    { label: "待支付订单", value: metrics?.pending_orders || 0 },
    { label: "处理中退款", value: metrics?.refund_processing || 0 },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">支付与订单仪表盘</h1>
          <p className="text-sm text-muted-foreground">用于运营观察和排障入口，数据实时读取后台聚合接口。</p>
        </div>
      </div>

      {loading ? <div className="text-sm text-muted-foreground">加载中...</div> : null}

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
        {kpis.map((kpi) => (
          <Card key={kpi.label}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{kpi.label}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-semibold">{kpi.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>订单状态分布（近7日）</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(metrics?.status_distribution || []).map((item) => (
              <div key={item.status} className="flex items-center justify-between border rounded-md p-2">
                <Badge variant="secondary">{item.status}</Badge>
                <span className="font-medium">{item.count}</span>
              </div>
            ))}
            {(metrics?.status_distribution || []).length === 0 ? <div className="text-sm text-muted-foreground">暂无数据</div> : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>支付渠道分布（近7日 paid）</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(metrics?.channel_distribution || []).map((item) => (
              <div key={item.channel} className="flex items-center justify-between border rounded-md p-2">
                <Badge>{item.channel || "unknown"}</Badge>
                <span className="font-medium">{item.count}</span>
              </div>
            ))}
            {(metrics?.channel_distribution || []).length === 0 ? <div className="text-sm text-muted-foreground">暂无数据</div> : null}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>最近退款记录</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(metrics?.recent_refunds || []).map((item) => (
            <div key={item.refund_no} className="border rounded-md p-3 text-sm grid grid-cols-1 md:grid-cols-4 gap-2">
              <div>
                <div className="text-muted-foreground">退款单号</div>
                <div className="font-medium">{item.refund_no}</div>
              </div>
              <div>
                <div className="text-muted-foreground">订单号</div>
                <div>{item.order_no}</div>
              </div>
              <div>
                <div className="text-muted-foreground">状态/金额</div>
                <div>{item.status} / {formatAmountFen(item.approved_amount_fen || item.requested_amount_fen || 0)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">更新时间</div>
                <div>{formatTime(item.updated_at)}</div>
              </div>
            </div>
          ))}
          {(metrics?.recent_refunds || []).length === 0 ? <div className="text-sm text-muted-foreground">暂无退款记录</div> : null}
        </CardContent>
      </Card>
    </div>
  );
}
